// components/ParametresPointage.jsx - PARTIE 1/2
import React, { useState, useEffect } from 'react';
import { 
  Clock, Save, RotateCcw, Loader2, Sun, Moon, Coffee, 
  AlertCircle, CheckCircle, Settings, Timer, TrendingUp, Fingerprint, Camera
} from 'lucide-react';
import api from '../services/api';
import { Toast } from './ui/Toast';
import { useLoader } from '../contexts/LoaderContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';


const ParametresPointage = () => {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [isDefault, setIsDefault] = useState(true);
  const { showLoader, hideLoader } = useLoader();
 

  // 🔥 Feature Flags
 const { isEnabled: rfidModeEnabled } = useFeatureFlag('rfid_attendance');
const { isEnabled: photoEnabled } = useFeatureFlag('pointage_photo');

  // États pour les plages horaires
  const [parametres, setParametres] = useState({
    arrivee_debut: '06:00',
    arrivee_fin: '10:00',
    depart_debut: '15:00',
    depart_fin: '23:00',
    mode_pause: 'automatique',
    mode_pointage: 'manuel', 
    pause_duree_auto: '01:00',
    pause_sortie_debut: '11:30',
    pause_sortie_fin: '14:00',
    pause_retour_debut: '12:00',
    pause_retour_fin: '15:00',
    arrivee_heure_prevue: '07:30',
    arrivee_tolerance_minutes: 20,
    depart_heure_prevue: '17:00',
    depart_tolerance_minutes: 10,
    pause_duree_prevue_minutes: 60,
    pause_tolerance_minutes: 10, 
    photo_obligatoire: false 
  });

  // ============================================================================
  // CHARGEMENT DES PARAMÈTRES
  // ============================================================================

  useEffect(() => {
    loadParametres();
  }, []);

 const loadParametres = async () => {
    showLoader();
    setLoading(true);
    try {
      const response = await api.parametresPointage.get();
      if (response.success) {
        setParametres(response.data);
        setIsDefault(response.isDefault || false);
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      showToast('Erreur de chargement', 'error');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const formatHeure = (heure) => {
    return heure?.replace(':', 'h') || '';
  };

  const calculerLimites = () => {
    const arriveeHeureParts = parametres.arrivee_heure_prevue.split(':');
    const arriveeHeureDecimal = parseInt(arriveeHeureParts[0]) + parseInt(arriveeHeureParts[1]) / 60;
    const arriveeLimiteATemps = arriveeHeureDecimal + (parametres.arrivee_tolerance_minutes / 60);
    const arriveeLimiteTolerance = arriveeLimiteATemps + (10 / 60);

    const departHeureParts = parametres.depart_heure_prevue.split(':');
    const departHeureDecimal = parseInt(departHeureParts[0]) + parseInt(departHeureParts[1]) / 60;
    const departLimiteAnticipe = departHeureDecimal - (parametres.depart_tolerance_minutes / 60);

    const formatDecimalToTime = (decimal) => {
      const h = Math.floor(decimal);
      const m = Math.round((decimal - h) * 60);
      return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
    };

    return {
      arrivee: {
        a_temps: `< ${formatDecimalToTime(arriveeLimiteATemps)}`,
        tolerance: `${formatDecimalToTime(arriveeLimiteATemps)} - ${formatDecimalToTime(arriveeLimiteTolerance)}`,
        retard: `${formatDecimalToTime(arriveeLimiteTolerance)} - ${formatHeure(parametres.arrivee_fin)}`,
        retard_grave: `> ${formatHeure(parametres.arrivee_fin)}`
      },
      depart: {
        a_temps: `≥ ${formatHeure(parametres.depart_heure_prevue)}`,
        anticipe: `${formatDecimalToTime(departLimiteAnticipe)} - ${formatHeure(parametres.depart_heure_prevue)}`,
        anticipe_grave: `< ${formatDecimalToTime(departLimiteAnticipe)}`
      },
      pause: {
        normale: `≤ ${parametres.pause_duree_prevue_minutes + parametres.pause_tolerance_minutes} min`,
        excessive: `> ${parametres.pause_duree_prevue_minutes + parametres.pause_tolerance_minutes} min`
      }
    };
  };

  // ============================================================================
  // GESTION DES CHANGEMENTS
  // ============================================================================

  const handleChange = (field, value) => {
    setParametres(prev => ({ ...prev, [field]: value }));
  };

  // ============================================================================
  // SAUVEGARDE ET RÉINITIALISATION
  // ============================================================================

  const handleSave = async () => {
    if (parametres.arrivee_debut >= parametres.arrivee_fin) {
      showToast('L\'heure de début d\'arrivée doit être avant l\'heure de fin', 'error');
      return;
    }

    if (parametres.depart_debut >= parametres.depart_fin) {
      showToast('L\'heure de début de départ doit être avant l\'heure de fin', 'error');
      return;
    }

    if (parametres.mode_pause === 'pointage') {
      if (parametres.pause_sortie_debut >= parametres.pause_sortie_fin) {
        showToast('L\'heure de début de sortie pause doit être avant l\'heure de fin', 'error');
        return;
      }
      if (parametres.pause_retour_debut >= parametres.pause_retour_fin) {
        showToast('L\'heure de début de retour pause doit être avant l\'heure de fin', 'error');
        return;
      }
    }

    setLoading(true);
    try {
      const response = await api.parametresPointage.save(parametres);
      if (response.success) {
        showToast('Paramètres enregistrés avec succès', 'success');
        setIsDefault(false);
        loadParametres();
      } else {
        showToast(response.message || 'Erreur lors de l\'enregistrement', 'error');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Voulez-vous vraiment réinitialiser les paramètres par défaut ?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.parametresPointage.reset();
      if (response.success) {
        showToast('Paramètres réinitialisés', 'success');
        loadParametres();
      }
    } catch (error) {
      console.error('Erreur réinitialisation:', error);
      showToast('Erreur lors de la réinitialisation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const limites = calculerLimites();

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-6">

      {/* MODE DE PAUSE */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Coffee size={20} style={{ color: '#448696' }} />
          <h3 className="text-lg font-bold text-gray-900">Mode de gestion des pauses</h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleChange('mode_pause', 'automatique')}
            className={`p-4 rounded-xl border-2 transition-all ${
              parametres.mode_pause === 'automatique'
                ? 'border-transparent shadow-sm'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            style={parametres.mode_pause === 'automatique' ? { backgroundColor: '#44869620' } : {}}
          >
            <div className="text-center">
              <Clock size={32} className={`mx-auto mb-2`} 
                style={{ color: parametres.mode_pause === 'automatique' ? '#448696' : '#9ca3af' }}
              />
              <div className="font-semibold text-gray-900">Automatique</div>
              <div className="text-xs text-gray-600 mt-1">Pause déduite automatiquement</div>
            </div>
          </button>

          <button
            onClick={() => handleChange('mode_pause', 'pointage')}
            className={`p-4 rounded-xl border-2 transition-all ${
              parametres.mode_pause === 'pointage'
                ? 'border-transparent shadow-sm'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            style={parametres.mode_pause === 'pointage' ? { backgroundColor: '#44869620' } : {}}
          >
            <div className="text-center">
              <Coffee size={32} className={`mx-auto mb-2`}
                style={{ color: parametres.mode_pause === 'pointage' ? '#448696' : '#9ca3af' }}
              />
              <div className="font-semibold text-gray-900">Pointage</div>
              <div className="text-xs text-gray-600 mt-1">4 pointages (sortie/retour pause)</div>
            </div>
          </button>
        </div>
      </div>

      
      {/* MODE DE POINTAGE */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Settings size={20} style={{ color: '#448696' }} />
          <h3 className="text-lg font-bold text-gray-900">Mode de pointage</h3>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Choisissez comment les employés effectuent leur pointage quotidien
        </p>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleChange('mode_pointage', 'manuel')}
            className={`p-4 rounded-xl border-2 transition-all ${
              parametres.mode_pointage === 'manuel'
                ? 'border-transparent shadow-sm'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            style={parametres.mode_pointage === 'manuel' ? { backgroundColor: '#fed77420' } : {}}
          >
            <div className="text-center">
              <Clock size={32} className={`mx-auto mb-2`} 
                style={{ color: parametres.mode_pointage === 'manuel' ? '#d4a843' : '#9ca3af' }}
              />
              <div className="font-semibold text-gray-900">Manuel</div>
              <div className="text-xs text-gray-600 mt-1">Saisie badge + bouton de validation</div>
            </div>
          </button>

            {/* 🔥 MODE RFID - Conditionnel */}
            {rfidModeEnabled && (
              <button
                onClick={() => handleChange('mode_pointage', 'rfid')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  parametres.mode_pointage === 'rfid'
                    ? 'border-transparent shadow-sm'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                style={parametres.mode_pointage === 'rfid' ? { backgroundColor: '#44869620' } : {}}
              >
                <div className="text-center">
                  <Fingerprint size={32} className={`mx-auto mb-2`}
                    style={{ color: parametres.mode_pointage === 'rfid' ? '#448696' : '#9ca3af' }}
                  />
                  <div className="font-semibold text-gray-900">RFID</div>
                  <div className="text-xs text-gray-600 mt-1">Badgeuse automatique (scan RFID)</div>
                </div>
              </button>
            )}


        </div>

        {/* Aperçu du mode sélectionné */}
        <div className="mt-4 p-4 rounded-xl" style={{ 
          backgroundColor: parametres.mode_pointage === 'manuel' ? '#fed77410' : '#44869610',
          borderLeft: '4px solid',
          borderLeftColor: parametres.mode_pointage === 'manuel' ? '#fed774' : '#448696'
        }}>
          <div className="flex items-start gap-3">
            <AlertCircle size={20} style={{ 
              color: parametres.mode_pointage === 'manuel' ? '#d4a843' : '#448696',
              flexShrink: 0,
              marginTop: '2px'
            }} />
            <div className="text-sm">
              {parametres.mode_pointage === 'manuel' ? (
                <>
                  <p className="font-semibold text-gray-900 mb-1">Mode Manuel</p>
                  <p className="text-gray-600">
                    Les employés saisissent leur numéro de badge et cliquent sur le bouton de pointage.
                    Interface complète avec grille des pointages effectués.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-gray-900 mb-1">Mode RFID</p>
                  <p className="text-gray-600">
                    Badgeuse professionnelle automatique : les employés approchent leur carte RFID du lecteur
                    et le pointage s'effectue instantanément sans aucune manipulation.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* CONFIGURATION ARRIVÉE */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Sun size={20} style={{ color: '#448696' }} />
          <h3 className="text-lg font-bold text-gray-900">Arrivée</h3>
        </div>

        <div className="space-y-6">
          {/* Plage horaire autorisée */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#44869620' }}>
            <label className="text-sm font-semibold text-gray-700 block mb-3">
              Plage horaire autorisée
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-600 block mb-2">Début</label>
                <input
                  type="time"
                  value={parametres.arrivee_debut}
                  onChange={(e) => handleChange('arrivee_debut', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                  style={{
                    borderColor: '#448696'
                  }}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-2">Fin</label>
                <input
                  type="time"
                  value={parametres.arrivee_fin}
                  onChange={(e) => handleChange('arrivee_fin', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                  style={{
                    borderColor: '#448696'
                  }}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(136, 185, 197, 0.1)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                />
              </div>
            </div>
          </div>

          {/* Configuration ponctualité */}
          <div className="p-4 rounded-xl border-2" style={{ backgroundColor: '#fed77420', borderColor: '#fed774' }}>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={18} style={{ color: '#d4a843' }} />
              <label className="text-sm font-semibold text-gray-700">
                Règles de ponctualité
              </label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-600 block mb-2">Heure d'arrivée prévue</label>
                <input
                  type="time"
                  value={parametres.arrivee_heure_prevue}
                  onChange={(e) => handleChange('arrivee_heure_prevue', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Heure normale de début de travail</p>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-2">Tolérance (minutes)</label>
                <input
                  type="number"
                  min="0"
                  max="60"
                  value={parametres.arrivee_tolerance_minutes}
                  onChange={(e) => handleChange('arrivee_tolerance_minutes', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Minutes avant retard officiel</p>
              </div>
            </div>

            {/* Aperçu des statuts */}
            <div className="mt-4 p-3 bg-white rounded-lg border" style={{ borderColor: '#fed77440' }}>
              <p className="text-xs font-semibold text-gray-700 mb-2">Statuts calculés automatiquement :</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#448696' }}></span>
                  <span className="font-medium">À temps :</span>
                  <span className="text-gray-600">{limites.arrivee.a_temps}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#fed774' }}></span>
                  <span className="font-medium">Tolérance :</span>
                  <span className="text-gray-600">{limites.arrivee.tolerance}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#fdac84' }}></span>
                  <span className="font-medium">Retard :</span>
                  <span className="text-gray-600">{limites.arrivee.retard}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  <span className="font-medium">Retard grave :</span>
                  <span className="text-gray-600">{limites.arrivee.retard_grave}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CONFIGURATION DÉPART */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Moon size={20} style={{ color: '#448696' }} />
          <h3 className="text-lg font-bold text-gray-900">Départ</h3>
        </div>

        <div className="space-y-6">
          {/* Plage horaire autorisée */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#44869620' }}>
            <label className="text-sm font-semibold text-gray-700 block mb-3">
              Plage horaire autorisée
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-600 block mb-2">Début</label>
                <input
                  type="time"
                  value={parametres.depart_debut}
                  onChange={(e) => handleChange('depart_debut', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-2">Fin</label>
                <input
                  type="time"
                  value={parametres.depart_fin}
                  onChange={(e) => handleChange('depart_fin', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Configuration ponctualité */}
          <div className="p-4 rounded-xl border-2" style={{ backgroundColor: '#fed77420', borderColor: '#fed774' }}>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={18} style={{ color: '#d4a843' }} />
              <label className="text-sm font-semibold text-gray-700">
                Règles de ponctualité
              </label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-600 block mb-2">Heure de départ prévue</label>
                <input
                  type="time"
                  value={parametres.depart_heure_prevue}
                  onChange={(e) => handleChange('depart_heure_prevue', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Heure normale de fin de travail</p>
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-2">Tolérance (minutes)</label>
                <input
                  type="number"
                  min="0"
                  max="60"
                  value={parametres.depart_tolerance_minutes}
                  onChange={(e) => handleChange('depart_tolerance_minutes', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Minutes pour départ anticipé acceptable</p>
              </div>
            </div>

            {/* Aperçu des statuts */}
            <div className="mt-4 p-3 bg-white rounded-lg border" style={{ borderColor: '#fed77440' }}>
              <p className="text-xs font-semibold text-gray-700 mb-2">Statuts calculés automatiquement :</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#448696' }}></span>
                  <span className="font-medium">À temps :</span>
                  <span className="text-gray-600">{limites.depart.a_temps}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#fed774' }}></span>
                  <span className="font-medium">Départ anticipé :</span>
                  <span className="text-gray-600">{limites.depart.anticipe}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  <span className="font-medium">Départ anticipé grave :</span>
                  <span className="text-gray-600">{limites.depart.anticipe_grave}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CONFIGURATION PAUSES */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Coffee size={20} style={{ color: '#fdac84' }} />
          <h3 className="text-lg font-bold text-gray-900">Pauses</h3>
        </div>

        {parametres.mode_pause === 'automatique' ? (
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#fdac8420' }}>
            <label className="text-sm font-semibold text-gray-700 block mb-3">
              Durée de pause automatique
            </label>
            <input
              type="time"
              value={parametres.pause_duree_auto}
              onChange={(e) => handleChange('pause_duree_auto', e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
            />
            <p className="text-xs text-gray-500 mt-2">
              Cette durée sera déduite automatiquement du temps de travail
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Plages horaires */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl" style={{ backgroundColor: '#fdac8420' }}>
                <label className="text-sm font-semibold text-gray-700 block mb-3">
                  Sortie pause
                </label>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-600 block mb-2">Début</label>
                    <input
                      type="time"
                      value={parametres.pause_sortie_debut}
                      onChange={(e) => handleChange('pause_sortie_debut', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-2">Fin</label>
                    <input
                      type="time"
                      value={parametres.pause_sortie_fin}
                      onChange={(e) => handleChange('pause_sortie_fin', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-xl" style={{ backgroundColor: '#44869620' }}>
                <label className="text-sm font-semibold text-gray-700 block mb-3">
                  Retour pause
                </label>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-600 block mb-2">Début</label>
                    <input
                      type="time"
                      value={parametres.pause_retour_debut}
                      onChange={(e) => handleChange('pause_retour_debut', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-2">Fin</label>
                    <input
                      type="time"
                      value={parametres.pause_retour_fin}
                      onChange={(e) => handleChange('pause_retour_fin', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration ponctualité pause */}
            <div className="p-4 rounded-xl border-2" style={{ backgroundColor: '#fed77420', borderColor: '#fed774' }}>
              <div className="flex items-center gap-2 mb-4">
                <Timer size={18} style={{ color: '#d4a843' }} />
                <label className="text-sm font-semibold text-gray-700">
                  Règles de ponctualité des pauses
                </label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-600 block mb-2">Durée prévue (minutes)</label>
                  <input
                    type="number"
                    min="0"
                    max="240"
                    value={parametres.pause_duree_prevue_minutes}
                    onChange={(e) => handleChange('pause_duree_prevue_minutes', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Durée normale de la pause</p>
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-2">Tolérance (minutes)</label>
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={parametres.pause_tolerance_minutes}
                    onChange={(e) => handleChange('pause_tolerance_minutes', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-semibold focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minutes supplémentaires acceptables</p>
                </div>
              </div>

              {/* Aperçu des statuts */}
              <div className="mt-4 p-3 bg-white rounded-lg border" style={{ borderColor: '#fed77440' }}>
                <p className="text-xs font-semibold text-gray-700 mb-2">Statuts calculés automatiquement :</p>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#448696' }}></span>
                    <span className="font-medium">Pause normale :</span>
                    <span className="text-gray-600">{limites.pause.normale}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="font-medium">Pause excessive :</span>
                    <span className="text-gray-600">{limites.pause.excessive}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      

      {/* 📸 PHOTO ANTI-FRAUDE */}
      {/* 🔥 PHOTO ANTI-FRAUDE - Conditionnel */}
      {photoEnabled && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Camera size={20} style={{ color: '#448696' }} />
            <h3 className="text-lg font-bold text-gray-900">Vérification par photo</h3>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Anti-fraude : exiger une photo de l'employé à chaque pointage pour empêcher 
            qu'un collègue pointe à sa place.
          </p>

          <div 
            className="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all"
            style={{ 
              backgroundColor: parametres.photo_obligatoire ? '#44869620' : '#f9fafb',
              border: '2px solid',
              borderColor: parametres.photo_obligatoire ? '#448696' : '#e5e7eb'
            }}
            onClick={() => handleChange('photo_obligatoire', !parametres.photo_obligatoire)}
          >
            <div className="flex items-center gap-3">
              <Camera size={24} style={{ 
                color: parametres.photo_obligatoire ? '#448696' : '#9ca3af' 
              }} />
              <div>
                <div className="font-semibold text-gray-900">
                  Photo obligatoire au pointage
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  L'employé devra se prendre en photo après avoir scanné son badge
                </div>
              </div>
            </div>
            
            {/* Toggle switch */}
            <div 
              className="relative w-12 h-7 rounded-full transition-all"
              style={{ backgroundColor: parametres.photo_obligatoire ? '#448696' : '#d1d5db' }}
            >
              <div 
                className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all"
                style={{ left: parametres.photo_obligatoire ? '22px' : '2px' }}
              />
            </div>
          </div>

          {parametres.photo_obligatoire && (
            <div className="mt-4 p-4 rounded-xl" style={{ 
              backgroundColor: '#fed77410',
              borderLeft: '4px solid #fed774'
            }}>
              <div className="flex items-start gap-3">
                <AlertCircle size={18} style={{ color: '#d4a843', flexShrink: 0, marginTop: '2px' }} />
                <div className="text-sm">
                  <p className="font-semibold text-gray-900 mb-1">Mode activé</p>
                  <p className="text-gray-600">
                    Après chaque scan de badge, une fenêtre caméra s'ouvrira. L'employé devra 
                    prendre une photo de son visage pour valider le pointage. La photo est 
                    sauvegardée comme preuve et vérifiée automatiquement (luminosité, netteté).
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}



      {/* BOUTONS D'ACTION */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex justify-between items-center">
          <button
            onClick={handleReset}
            disabled={loading || isDefault}
            className="px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <RotateCcw size={18} className="inline mr-2" />
            Réinitialiser
          </button>

          <button
            onClick={handleSave}
            disabled={loading}
            className="px-8 py-3 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 transition-all"
            style={{ backgroundColor: '#448696' }}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="inline mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={18} className="inline mr-2" />
                Enregistrer les paramètres
              </>
            )}
          </button>
        </div>
      </div>

      {/* TOAST */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })}
        />
      )}


     

    </div>
  );
};

export default ParametresPointage;